/*------------------------------------------------------------------------------------------
* © 2020 KPMG LLP, a Delaware limited liability partnership and 
* a member firm of the KPMG global organization of independent member firms 
* affiliated with KPMG International Limited, a private English company limited by guarantee. 
* All rights reserved.
*-----------------------------------------------------------------------------------------*/


import { KeyValuePair, KeyValuePairWithProperty } from './project.model';

//#region Enums for quick and centralized access
// App Mode Keys
export enum AppMode {
    evsMode = '1',
    taxMode = '2'
}

// Local Storage Keys
export enum StorageKey {
    ApplicationType = 'ApplicationType',
    Companies = 'Companies',
    UserRoles = 'UserRoles',
    IsRefreshToken='isRefreshToken',
    AngularRoute='angularRoute',
    QueryString='queryString',
    LastAction='lastAction'
}

export enum Texts {
    YTD='YTD',
    Other = 'Other',
    Cash = 'Cash',
    PurchasePrice = 'Purchase Price',
    GoodWill='Goodwill',
    DefaultConceptTag = 'Custom Tag',
    NotFoundVerbiage = 'No filing information available',   
    IntangibleTabName = 'Intangibles',
    GoodWillTabName = 'Goodwill/PP&E',
    EnterpriseTabName = 'Enterprise Value',
    CapitalIQTabName = 'Capital IQ',
    PurchasePriceRankChangedComment = 'Purchase Price Rank Changed',
    IncomeStatementTab = 'Income Statement',// Tightly Coupled with TaxFactViewer
    BalanceSheetTab = 'Balance Sheet', // Tightly Coupled with TaxFactViewer
    IncomeTaxDisclosureTab = 'Income Tax Disclosure',// Tightly Coupled with TaxFactViewer
    NotesKey = 'notes', // Key For Footnote Navigation cnstTaxMappingTabs and FootNote Report Generation
    NotesBlobFolder='Notes', //Folder name in Container
    StatementsKey = 'statements',
    StatementsBlobFolder='Statements',//Folder name in container
    KeyWord = 'keyword', // Key For KeyWord Report Generation
    WarningPrefix = 'An error occurred while ', // append specific message which action is performed.
    NotApplicableShort = 'NA',
    Evs = 'evs',
    Tax = 'tax',
    AscendingShort = 'asc',
    DescendingShort = 'desc',
    FinancialStatementReportName = 'Financial statements and footnote extraction',
    Form8kReports = '8-K Reports and Exhibits',
    Word = 'word', // For Report Type
    Zip = 'zip', // For Report Type
    Excel = 'excel', // For Report Type
    Powerbi = 'powerbi', // For Report Type
    MyProjects = 'My Projects', // Text Value For cnstProjectTabNames
    ShareProjects = 'Shared Projects', // Text Value For cnstProjectTabNames
    AllProjects = 'All Projects', // Text Value For cnstProjectTabNames
    ArchivedProjects = 'Archived', // Text Value For cnstProjectTabNames
    Validate='Validate',
    Review='Review',
    EnterprisePreamble='Enterprise Value Inputs should include Purchase Price, Debt (including ST and LT interest bearing debt and capital lease obligations), Cash, Non-Controlling Interest, and Preferred Interest, whenever available.',
    Form8KReportCode = '8kreportsandexhibits',
    TenKFormType='10-k',
    TenQFormType='10-q',
    EightKFormType='8-k',
    Required='required',
    Optional='optional',
    SECClassification='SEC Classification',
    CIQClassification='CIQ Classification',
    KPMGClassification='KPMG Classification'
   
}

export enum ToolTips {

    // Search For Tooltips.{Name of Enum} before changing , These are used directly in HTML
    filingDateFrom='Filing period may include transactions outside of the filing period',
    evsValidationStatus='Unvalidated transactions can be validated by user in the See Detail link',
    sharedWith='Enter the e-mail addresses of the people you would like to share this project with',
    customizeMapping='Sort transactions using labels in  below table headers.',
    generateReport ='To Generate reports of transaction details to export (Excel, PowerBI, 10K Extract)',
    rul='If a range is available, use the mid-point of the range as Final RUL',
    finalFairValue='Use unrounded value',
    ciqInfoOnly='This particular result has only Capital IQ information',
    fullReport='Toggle to see entire filing or section of the transaction disclosures (if available)',
    // fullReport='Toggle to see entire 10-K or 10-K section of the transaction disclosures (if available)',
    projectMapping='Refer to User Guide ‘Intangible Asset Categories’ section for a list of intangible asset categories available',
    finalMapping='Select from the drop-down menu to assign the tab to the correct intangible asset',
    addAnotherRow='To add an additional line to the list (i.e. in instances where the XBRL tagging is unavailable)',
    purchasepriceFairValue='Select the correct value associated with the purchase price',
    evsCompanyName='Select the acquisitions for export by checking the boxes below and click “Save Selections”. This will add the transactions into the “Generate Report” screen',
    deleteProject='You can archive this project which will move the project to the archived tab. If you have shared this project, those users will no longer be able to access this project until restored.',
    searchInfo='Double click on Search to quickly change the name and use escape to come out of edit mode.',
    keywordCriteriaInfo='keywords are case insensitive and wild card characters ae not allowed',
    intangibleAssetInfo=`-Asset Categories are in order of least specific to most specific 
    -User input number represents % of BEV (i.e. user input 55 = 55% of BEV)`,
    capIQInfo='User can type or paste individual or a list of transaction IDs separated by a space'
}

export enum Actions{
    Next='Next',
    Previous='Previous',
    Close='Close'
}
export enum Urls {
    accessDenied = '/access-denied',
}
export enum Status {
    InProgress = 'InProgress',
    Completed = 'Completed',
    Failed = 'Failed'
}
export enum TaxReportCode {
    KeyWordSearch = 'keywordsearchresults',
    FinancialStatement = 'financialstatementdatabase',
    StatementPowerBi = 'financialstatementdatabasePowerBI',
    TrendingPowerBi = 'financialstatementdatabaseTrending',
    StatementExcel = 'financialstatementdatabaseexcel',
    FinancialExtract = 'financialextract',
    FullReports = 'fullreports',
    Form8KReports = '8kreportsandexhibits'
}

export enum FileNames{
    EVSUserGuideName= 'Intangible Spotlight Userguide.pdf',
    TaxUserGuideName= '10-K Spotlight Userguide.pdf',
}

export enum ValidationStatus{
    Unvalidated = "Unvalidated",
    Validated = "Validated", 
    Reviewed = "Reviewed"
}

export enum Operators{
    And = "AND",
    Or = "OR", 
    
}

export enum Conditions{
    isEqualTo = "is equal to",
    isGreaterThan = "is greater than",
    isLesserThan = "is less than",
    isBetween = "is between",
    isGreaterThanOrEqualtTo = "is greater than or equal to",
    isLesserThanOrEqualtTo = "is less than or equal to",
    contains = "contains",
    doesNotContains = "does not contain",
    
}

export enum IntangibleAssetCatLevels{
    PlaceHolder = "--Select Asset Category Level--",
    Level1CategoryName = "Parent Category", 
    Level2CategoryName = "Asset Category",
    AssetCatGroupName = "Specific Asset Name",
}

export enum EVSSearchColumn{  
    BuyerSic= 'BuyerIndustryID', //Does  not represent actual column name
    BuyerCompnay= 'CompanyName',
    BuyerCIK= 'Cik', 
    FilingDates= 'DateFiled', 
    FilingTypes= 'FormTypeId',
    TargetCompany= 'AcquiredEntity',
    // TargetCompany= 'Target',
    TargetSic= 'CIQTargetIQPrimaryIndustryCode',
    TransactionClosedDate= 'CIQClosedDate',
    TargetDescription= 'CIQTargetBusinessDescription',
    CapIQTransactionId= 'CIQTransactionId',
    EnterPriseValue='EnterPriseValue',
    Goodwill= 'GoodWill', 
    TargetRevenue= 'CIQTargetLTMFinTotalRevenue',
    TargetEBITDA= 'CIQTargetLTMFinEBITDA',
    TargetEBIT= 'CIQTargetLTMFinEBIT', 
    SECIndustry='SECIndustryTitle',
    IQPrimaryIndustry='CIQPrimaryIndustry',
    KPMGIndsutry='KPMGClassification',
    IntangibleAssetCat = 'GroupId'
}

//#endregion

//#region Constants for quick and centralized access for non heirarchial complex values
export const cnstTaxMappingTabs: KeyValuePair[] = [{ key: 'mapping', value: 'Financial Data Extraction' },
{ key: 'notes', value: 'Financial Statements And Footnote Extraction' } //old value : Footnote Navigation
];

export const cnst8KFactViewerTabs: KeyValuePair[] = [{ key: 'factviewer', value: 'Fact Viewer' }];
export const cnstProjectTabNames: string[] = [Texts.AllProjects,Texts.MyProjects, Texts.ShareProjects, Texts.ArchivedProjects];
export const cnstReportIcons: KeyValuePair[] = [{ key: 'word', value: 'fa fa-file-word-o' },
{ key: 'zip', value: 'fa fa-file-archive-o' },
{ key: 'powerbi', value: 'fa fa-file-o' },
{ key: 'pdf', value: 'fa fa-file-pdf-o' },
{ key: 'excel', value: 'fa fa-file-excel-o' },

];

export const cnstSectionTypes: string[]=['IncomeTaxDisclosure','IncomeStatement','BalanceSheet']
export const cnstEVSQuarters:any[]=[{fiscalPeriod:'1Q',quarterName:'1Q'},{fiscalPeriod:'2Q',quarterName:'2Q'},{fiscalPeriod:'3Q',quarterName:'3Q'}];
export const cnstTaxQuarters:any[]=[{fiscalPeriod:'1Q',quarterName:'1Q YTD'},{fiscalPeriod:'2Q',quarterName:'2Q YTD'},{fiscalPeriod:'3Q',quarterName:'3Q YTD'}];

export const cnstEVSSearchCriteria: KeyValuePairWithProperty[] = [{ 
     key: EVSSearchColumn.CapIQTransactionId, value: 'Capital IQ Transaction ID',
     distinguisher:Texts.Required },

{key:  EVSSearchColumn.BuyerSic, value: "Buyer Company Industry",distinguisher:Texts.Required },
{ key: EVSSearchColumn.BuyerCompnay, value: 'Buyer Company Name' ,distinguisher:Texts.Required },
{ key: EVSSearchColumn.BuyerCIK, value: 'Buyer CIK', distinguisher:Texts.Required},
{ key: EVSSearchColumn.TargetSic, value: "Target Company Industry", distinguisher:Texts.Required },
{ key: EVSSearchColumn.TargetCompany, value: 'Target Company Name' , distinguisher:Texts.Required},
{ key: EVSSearchColumn.TransactionClosedDate, value: 'Transaction Closed Date' , distinguisher:Texts.Optional},
{ key: EVSSearchColumn.TargetDescription, value: 'Target Company Description Keyword', distinguisher:Texts.Optional },
{ key: EVSSearchColumn.EnterPriseValue, value: 'Enterprise Value', distinguisher:Texts.Optional },
{ key: EVSSearchColumn.Goodwill, value: 'Goodwill Amount', distinguisher:Texts.Optional },
{ key: EVSSearchColumn.TargetRevenue, value: 'Target Company Revenue (at announcement)', distinguisher:Texts.Optional },
{ key: EVSSearchColumn.TargetEBITDA, value: 'Target Company EBITDA (at announcement)', distinguisher:Texts.Optional },
{ key: EVSSearchColumn.TargetEBIT, value: 'Target Company EBIT (at announcement)', distinguisher:Texts.Optional },
{ key: EVSSearchColumn.IntangibleAssetCat, value: 'Intangible Asset Category and % of BEV', distinguisher:Texts.Optional },
];


//#endregion
